import { useState, useEffect, useCallback } from 'react';
import { Activity, Wifi, WifiOff, RefreshCw, AlertTriangle, Power } from 'lucide-react';
import { api } from '../../../api/client';

interface CooldownStatus {
  active: boolean;
  reason: string | null;
  released_at: string | null;
}
interface ThrottleState {
  current_rate: number;
  items_current_rate: number;
  seller_rate_max: number;
  seller_current_rate_max: number;
}
interface SourceRow {
  source: string;
  label: string;
  rate: number;
  lane: 'mass' | 'interactive';
}
interface MlControl {
  cooldown: CooldownStatus;
  throttle: ThrottleState;
  egress: { ip: string; role: string; blocked: boolean } | null;
  sources: SourceRow[];
  throttle_config: Record<string, number>;
  throttle_ranges: Array<{ key: string; label: string; min: number; max: number; recommended: number; advanced: boolean }>;
}

function ThrottleKnobs({
  config,
  ranges,
  onSaved,
}: {
  config: Record<string, number>;
  ranges: Array<{ key: string; label: string; min: number; max: number; recommended: number; advanced: boolean }>;
  onSaved: () => void;
}) {
  const [vals, setVals] = useState<Record<string, number>>(config);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'warn' | 'err'; text: string } | null>(null);

  useEffect(() => { setVals(config); }, [config]);

  const basic = ranges.filter((r) => !r.advanced);
  const advanced = ranges.filter((r) => r.advanced);
  const dirty = ranges.some((r) => vals[r.key] !== config[r.key]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    const payload: Record<string, number> = {};
    for (const r of ranges) if (vals[r.key] !== config[r.key]) payload[r.key] = Number(vals[r.key]);
    const res = await api.put<{ config: Record<string, number>; warnings: string[] }>(
      '/admin/ml-control/throttle',
      payload
    );
    setBusy(false);
    if (!res.success) {
      setMsg({ type: 'err', text: (res as { error?: string }).error || 'No se pudo guardar' });
      return;
    }
    const warnings = (res.data as { warnings?: string[] })?.warnings || [];
    setMsg(warnings.length ? { type: 'warn', text: warnings.join(' · ') } : { type: 'ok', text: 'Guardado y aplicado' });
    onSaved();
  };

  const reset = async () => {
    setBusy(true);
    setMsg(null);
    const res = await api.post('/admin/ml-control/throttle/reset', {});
    setBusy(false);
    if (res.success) {
      setMsg({ type: 'ok', text: 'Volvimos a los valores recomendados' });
      onSaved();
    }
  };

  const Knob = (r: { key: string; label: string; min: number; max: number; recommended: number }) => {
    const v = vals[r.key];
    const over = typeof v === 'number' && v > r.recommended;
    return (
      <div key={r.key} className="flex items-center justify-between gap-3 py-2">
        <div className="text-sm text-[var(--color-text)]">
          {r.label}
          <span className="text-xs text-[var(--color-text-muted)] ml-2">
            (rango {r.min}–{r.max}, recomendado {r.recommended})
          </span>
        </div>
        <input
          type="number"
          min={r.min}
          max={r.max}
          value={v ?? ''}
          onChange={(e) => setVals((s) => ({ ...s, [r.key]: Number(e.target.value) }))}
          className={`w-28 px-2 py-1 rounded border text-sm text-right bg-[var(--color-bg)] ${
            over ? 'border-amber-500 text-amber-600' : 'border-[var(--color-border)] text-[var(--color-text)]'
          }`}
        />
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">El ritmo (límites)</h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-3">
        Cambiá un número y toma efecto en segundos, sin reiniciar. Si ponés un valor por encima del recomendado, te avisamos: más velocidad = más riesgo de que Mercado Libre bloquee.
      </p>
      <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
        {basic.map((r) => Knob(r))}
      </div>

      <button
        onClick={() => setShowAdvanced((s) => !s)}
        className="mt-3 text-xs text-[var(--color-text-muted)] underline"
      >
        {showAdvanced ? 'Ocultar avanzado' : 'Mostrar avanzado (riesgoso)'}
      </button>
      {showAdvanced && (
        <div className="mt-2 rounded-lg border border-amber-500/50 bg-amber-500/5 p-4">
          <div className="flex items-center gap-1.5 text-xs text-amber-600 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Estas perillas son finas. Tocalas solo si sabés lo que hacés.
          </div>
          <div className="divide-y divide-[var(--color-border)]">{advanced.map((r) => Knob(r))}</div>
        </div>
      )}

      {msg && (
        <div
          className={`mt-3 text-xs ${
            msg.type === 'err' ? 'text-red-600' : msg.type === 'warn' ? 'text-amber-600' : 'text-green-600'
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          disabled={busy || !dirty}
          onClick={save}
          className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
        >
          Guardar cambios
        </button>
        <button
          disabled={busy}
          onClick={reset}
          className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] text-xs font-medium hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
        >
          Volver a los valores recomendados
        </button>
      </div>
    </div>
  );
}

function EgressPanel() {
  const [d, setD] = useState<{ active_role: string | null; ips: { primary: { ip: string; blocked_at: string | null } | null; backup: { ip: string } | null } } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get('/admin/egress');
    if (r.success) setD(r.data as never);
  }, []);
  useEffect(() => { load(); }, [load]);
  const sw = async (role: 'primary' | 'backup') => {
    setBusy(true);
    await api.post('/admin/egress/switch', { role });
    setBusy(false);
    load();
  };
  if (!d) return null;
  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Línea de salida (IP)</h3>
      <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] flex items-center justify-between gap-3">
        <div className="text-sm text-[var(--color-text)]">
          Saliendo por: <b>{d.active_role === 'primary' ? 'Principal' : 'Respaldo'}</b>
          <div className="text-xs text-[var(--color-text-muted)]">
            Principal: {d.ips.primary?.ip} {d.ips.primary?.blocked_at ? '(bloqueada)' : '(ok)'} · Respaldo: {d.ips.backup?.ip}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={busy} onClick={() => sw('primary')} className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-xs disabled:opacity-50">Usar principal</button>
          <button disabled={busy} onClick={() => sw('backup')} className="px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-600 text-xs disabled:opacity-50">Forzar respaldo</button>
        </div>
      </div>
    </div>
  );
}

function ProcessGrid({ sellerOverrides }: { sellerOverrides: Record<string, number> }) {
  const [d, setD] = useState<{ rows: Array<{ seller_id: number; feature_key: string; enabled: boolean }>; sellers: Array<{ id: number; nickname: string }> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState<Record<number, number>>({});
  const load = useCallback(async () => {
    const r = await api.get('/admin/ml-control/processes');
    if (r.success) setD(r.data as never);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const init: Record<number, number> = {};
    for (const [k, v] of Object.entries(sellerOverrides || {})) init[Number(k)] = v as number;
    setRates(init);
  }, [sellerOverrides]);
  if (!d) return null;
  const features = Array.from(new Set(d.rows.map((r) => r.feature_key)));
  const isOn = (seller: number, feat: string) => d.rows.find((r) => r.seller_id === seller && r.feature_key === feat)?.enabled ?? false;
  const toggle = async (seller: number, feat: string, val: boolean) => {
    setBusy(true);
    await api.put('/admin/ml-control/processes', { seller_id: seller, feature_key: feat, enabled: val });
    setBusy(false);
    load();
  };
  const saveRate = async (seller: number) => {
    const v = rates[seller];
    if (Number.isFinite(v)) await api.put(`/admin/ml-control/throttle/seller/${seller}`, { rateMax: Number(v) });
  };
  const clearRate = async (seller: number) => { await api.delete(`/admin/ml-control/throttle/seller/${seller}`); };
  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Procesos por vendedor</h3>
      <div className="rounded-lg border border-[var(--color-border)] overflow-auto max-h-[420px]">
        <table className="w-full text-xs">
          <thead className="bg-[var(--color-surface)] sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">Proceso</th>
              {d.sellers.map((s) => <th key={s.id} className="px-2 py-2">{s.nickname}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-[var(--color-border)] bg-[var(--color-bg)]">
              <td className="px-2 py-2 font-medium text-[var(--color-text)]">Techo de ritmo (llamadas/min)</td>
              {d.sellers.map((s) => (
                <td key={s.id} className="px-2 py-2 text-center">
                  <input type="number" value={rates[s.id] ?? ''} placeholder="auto"
                    onChange={(e) => setRates((x) => ({ ...x, [s.id]: Number(e.target.value) }))}
                    onBlur={() => (rates[s.id] ? saveRate(s.id) : clearRate(s.id))}
                    className="w-20 px-1 py-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-right" />
                </td>
              ))}
            </tr>
            {features.map((f) => (
              <tr key={f} className="border-t border-[var(--color-border)]">
                <td className="px-2 py-2 text-[var(--color-text)]">{f}</td>
                {d.sellers.map((s) => (
                  <td key={s.id} className="px-2 py-2 text-center">
                    <input type="checkbox" disabled={busy} checked={isOn(s.id, f)} onChange={(e) => toggle(s.id, f, e.target.checked)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllowlistPanel() {
  const [feats, setFeats] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get<{ features: string[] }>('/admin/ml-control/allowlist');
    if (r.success && r.data) { setFeats((r.data as { features: string[] }).features); setText((r.data as { features: string[] }).features.join(', ')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    const features = text.split(',').map((s) => s.trim()).filter(Boolean);
    await api.put('/admin/ml-control/allowlist', { features });
    setBusy(false);
    load();
  };
  return (
    <div>
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">Qué sigue andando en una pausa de emergencia</h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-2">Lista de procesos (separados por coma) que NO se frenan aunque Mercado Libre esté en pausa. Vacío = vuelve a la lista por defecto.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
        className="w-full px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-text)]" />
      <div className="mt-2"><button disabled={busy} onClick={save} className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs disabled:opacity-50">Guardar lista</button>
        <span className="text-xs text-[var(--color-text-muted)] ml-2">Actual: {feats.length} procesos</span></div>
    </div>
  );
}

// ── PALANCA MAESTRA: el freno de mano. Frena TODO (salvo la lista de abajo), controlado. ──
function HandbrakeSwitch({ cooldown, onChanged }: { cooldown: CooldownStatus; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const active = cooldown.active;
  const toggle = async (action: 'pause' | 'resume') => {
    setBusy(true);
    await api.post('/admin/ml-cooldown', { action });
    setBusy(false);
    onChanged();
  };
  return (
    <div
      className={`rounded-lg border-2 p-4 flex items-center justify-between gap-4 ${
        active ? 'border-red-500 bg-red-500/10' : 'border-[var(--color-border)] bg-[var(--color-surface)]'
      }`}
    >
      <div className="flex items-center gap-3">
        <Power className={`w-7 h-7 ${active ? 'text-red-600' : 'text-green-600'}`} />
        <div>
          <div className="text-sm font-semibold text-[var(--color-text)]">Freno de mano de Mercado Libre</div>
          <div className="text-xs text-[var(--color-text-muted)] max-w-xl">
            {active
              ? 'TODO FRENADO. Solo siguen andando los procesos de la lista "Qué sigue andando en una pausa". Al soltar el freno, reactiva de a poco (controlado).'
              : 'Todo andando normal, con el ritmo regulado. Tirá del freno para frenar todo de golpe (de forma controlada).'}
          </div>
        </div>
      </div>
      {!active ? (
        <button
          disabled={busy}
          onClick={() => toggle('pause')}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap"
        >
          <AlertTriangle size={16} /> Frenar todo
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={() => toggle('resume')}
          className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap"
        >
          <RefreshCw size={16} /> Soltar el freno
        </button>
      )}
    </div>
  );
}

// ── PALANCA OPUESTA: desactivar TODA la regulación (correr a full, sin límites). RIESGOSO. ──
function RegulationSwitch({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (val: boolean) => {
    setBusy(true);
    await api.put('/admin/ml-control/regulation', { enabled: val });
    setBusy(false);
    onChanged();
  };
  return (
    <div
      className={`rounded-lg border-2 p-4 flex items-center justify-between gap-4 ${
        enabled ? 'border-[var(--color-border)] bg-[var(--color-surface)]' : 'border-red-500 bg-red-500/10'
      }`}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className={`w-7 h-7 ${enabled ? 'text-[var(--color-text-muted)]' : 'text-red-600'}`} />
        <div>
          <div className="text-sm font-semibold text-[var(--color-text)]">
            Regulación de ritmo (freno + carriles + techos por vendedor)
          </div>
          <div className="text-xs text-[var(--color-text-muted)] max-w-xl">
            {enabled
              ? 'ACTIVA: todas las llamadas pasan por el freno, los carriles y los techos. Es lo seguro.'
              : '⚠️ DESACTIVADA: el sistema corre A FULL, sin ningún límite. Riesgo ALTO de que Mercado Libre bloquee la IP. Usalo solo si sabés lo que hacés y por poco tiempo.'}
          </div>
        </div>
      </div>
      {enabled ? (
        <button
          disabled={busy}
          onClick={() => toggle(false)}
          className="px-4 py-2 rounded-lg border-2 border-red-500 text-red-600 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-50 whitespace-nowrap"
        >
          Desactivar límites (a full)
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={() => toggle(true)}
          className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        >
          Reactivar la regulación
        </button>
      )}
    </div>
  );
}

// Velocidad del drenado de avisos — perillas ajustables EN CALIENTE (app_config).
function DrainTuningKnobs() {
  type Knob = { key: string; label: string; min: number; max: number; value: number };
  const [knobs, setKnobs] = useState<Knob[]>([]);
  const [vals, setVals] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ knobs: Knob[] }>('/admin/ml-control/drain-tuning');
      const k: Knob[] = res.data?.knobs || [];
      setKnobs(k);
      setVals(Object.fromEntries(k.map((x) => [x.key, x.value])));
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const dirty = knobs.some((k) => vals[k.key] !== k.value);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/admin/ml-control/drain-tuning', vals);
      setMsg({ type: 'ok', text: 'Guardado. Toma efecto en ~5–15 segundos, sin reiniciar.' });
      await load();
    } catch {
      setMsg({ type: 'err', text: 'No se pudo guardar.' });
    } finally {
      setBusy(false);
    }
  };

  if (knobs.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">
        Velocidad del drenado de avisos
      </h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-3">
        Controlan qué tan rápido se procesan los avisos de Mercado Libre. Toman efecto en segundos,
        sin reiniciar. ⚠️ Más velocidad = más riesgo de bloqueo — sobre todo las{' '}
        <strong>conexiones simultáneas</strong>, que es lo que el WAF de Mercado Libre mira.
      </p>
      <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
        {knobs.map((k) => {
          const v = vals[k.key];
          const over = k.key === 'ml_global_concurrency' && v > 10;
          return (
            <div key={k.key} className="flex items-center justify-between gap-3 py-2">
              <div className="text-sm text-[var(--color-text)]">
                {k.label}
                <span className="text-xs text-[var(--color-text-muted)] ml-2">
                  (rango {k.min}–{k.max})
                </span>
              </div>
              <input
                type="number"
                min={k.min}
                max={k.max}
                value={v ?? ''}
                onChange={(e) => setVals((s) => ({ ...s, [k.key]: Number(e.target.value) }))}
                className={`w-28 px-2 py-1 rounded border text-sm text-right bg-[var(--color-bg)] ${
                  over
                    ? 'border-amber-500 text-amber-600'
                    : 'border-[var(--color-border)] text-[var(--color-text)]'
                }`}
              />
            </div>
          );
        })}
      </div>
      {msg && (
        <div className={`mt-3 text-xs ${msg.type === 'err' ? 'text-red-600' : 'text-green-600'}`}>
          {msg.text}
        </div>
      )}
      <div className="mt-3">
        <button
          disabled={busy || !dirty}
          onClick={save}
          className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
        >
          Guardar velocidad
        </button>
      </div>
    </div>
  );
}

export function MlControlTab() {
  const [data, setData] = useState<MlControl | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const res = await api.get<MlControl>('/admin/ml-control');
    if (res.success && res.data) setData(res.data as MlControl);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-[var(--color-text-muted)]">
        <RefreshCw className="w-4 h-4 animate-spin" /> Cargando…
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-[var(--color-text-muted)]">No se pudo cargar el estado.</div>;
  }

  const cd = data.cooldown;
  const estado = !cd.active
    ? { label: 'Andando', color: '#16a34a' }
    : cd.released_at
      ? { label: 'Reactivando de a poco', color: '#d97706' }
      : cd.reason === 'manual'
        ? { label: 'Pausa manual', color: '#d97706' }
        : { label: 'Enfriando (ML bloqueó)', color: '#dc2626' };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-[var(--color-text)] flex items-center gap-2">
        <Activity className="w-5 h-5" /> Conexión y Frenos de Mercado Libre
      </h2>

      {/* PALANCA MAESTRA — Freno de mano (frenar todo) */}
      <HandbrakeSwitch cooldown={data.cooldown} onChanged={load} />

      {/* PALANCA OPUESTA — Desactivar la regulación (correr a full) */}
      <RegulationSwitch
        enabled={(data.throttle_config as unknown as { regulationEnabled?: boolean }).regulationEnabled !== false}
        onChanged={load}
      />

      {/* Zona 0 — Semáforo */}
      <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="text-xs text-[var(--color-text-muted)]">Conexión</div>
          <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: estado.color }}>
            {cd.active ? <WifiOff className="w-4 h-4" /> : <Wifi className="w-4 h-4" />}
            {estado.label}
          </div>
        </div>
        <div>
          <div className="text-xs text-[var(--color-text-muted)]">Línea de salida</div>
          <div className="text-sm font-medium text-[var(--color-text)]">
            {data.egress ? (data.egress.role === 'primary' ? 'Principal' : 'Respaldo') : '—'}
            {data.egress?.blocked ? ' (bloqueada)' : ''}
          </div>
        </div>
        <div>
          <div className="text-xs text-[var(--color-text-muted)]">Ritmo por vendedor</div>
          <div className="text-sm font-medium text-[var(--color-text)]">
            {data.throttle.seller_current_rate_max} / {data.throttle.seller_rate_max} por min
          </div>
        </div>
        <div>
          <div className="text-xs text-[var(--color-text-muted)]">Ritmo listados</div>
          <div className="text-sm font-medium text-[var(--color-text)]">
            {data.throttle.items_current_rate} por min
          </div>
        </div>
      </div>

      {/* Zona 0.5 — Quién le habla a ML ahora */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">
          Quién le está hablando a Mercado Libre ahora
        </h3>
        {data.sources.length === 0 ? (
          <div className="text-sm text-[var(--color-text-muted)] flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Sin llamadas en el último minuto.
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--color-border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-[var(--color-text-muted)] text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Proceso</th>
                  <th className="text-right px-3 py-2">Llamadas/min</th>
                  <th className="text-right px-3 py-2">Carril</th>
                </tr>
              </thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={s.source} className="border-t border-[var(--color-border)]">
                    <td className="px-3 py-2 text-[var(--color-text)]">{s.label}</td>
                    <td className="px-3 py-2 text-right font-medium text-[var(--color-text)]">{s.rate}</td>
                    <td className="px-3 py-2 text-right text-[var(--color-text-muted)]">
                      {s.lane === 'mass' ? 'Fondo' : 'En vivo'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Zona 1 — Perillas del ritmo */}
      <ThrottleKnobs
        config={data.throttle_config}
        ranges={data.throttle_ranges}
        onSaved={load}
      />
      {/* Zona 1b — Velocidad del drenado (perillas en caliente) */}
      <DrainTuningKnobs />
      <ProcessGrid sellerOverrides={(data.throttle_config as unknown as { sellerOverrides?: Record<string, number> }).sellerOverrides || {}} />
      <AllowlistPanel />
      <EgressPanel />
    </div>
  );
}
